import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Lock,
  Linkedin,
  Instagram,
  Briefcase,
  FileText,
  Download,
  Mic2,
  Loader2,
} from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { HtmlContent } from '@/components/ui/html-content'
import { Button } from '@/components/ui/button'
import { LevelIcon } from '@/components/ui/LevelIcon'
import { CourseTrailer } from '@/components/CourseTrailer'
import YouTubePlayer from '@/components/YouTubePlayer'
import { useEpisodeProgress } from '@/hooks/use-progress'
import { formatDuration } from '@/utils/duration'
import type { Course } from '@/types/course'

interface MasterclassDetailViewProps {
  course: Course
  isEnrolled: boolean | undefined
  isAuthenticated: boolean
  enrollPending: boolean
  onEnroll: () => void
  allEpisodesCompleted: boolean | undefined
}

const levelLabels: Record<string, string> = {
  beginner: 'Iniciante',
  intermediate: 'Intermediário',
  advanced: 'Avançado',
  expert: 'Especialista',
}

/**
 * Página dedicada de Masterclass. Renderizada via early-return em
 * CourseDetail quando `course.type === 'masterclass'`. Layout próprio
 * (vídeo + meta + 2 cards Mentor/Workbook) pra não inflar a página
 * regular com if/else.
 *
 * Diferenças vs CourseDetail regular:
 * - SEM módulos/episódios/stepper (já é vídeo único)
 * - SEM "Comece agora" lateral (CTA único integrado abaixo do vídeo)
 * - COM 2 cards lado a lado: Mentor + Workbook
 *
 * Mentor é obrigatório em masterclass (Zod refine), mas cursos legacy
 * podem ter `mentor=null` — exibimos fallback discreto pra não quebrar.
 */
export function MasterclassDetailView({
  course,
  isEnrolled,
  isAuthenticated,
  enrollPending,
  onEnroll,
  allEpisodesCompleted,
}: MasterclassDetailViewProps) {
  // Masterclass tem 1 vídeo só — primeiro episódio do primeiro módulo
  const firstModule = course.modules?.[0]
  const firstEpisode = firstModule?.episodes?.[0]
  const videoUrl = firstEpisode?.videoUrl
  const workbookUrl = firstEpisode?.workbookUrl ?? null
  const workbookCover = firstEpisode?.workbookCoverUrl ?? null

  // `coming_soon`: aula ainda não foi liberada. Mesmo que o usuário esteja
  // matriculado (auto-enroll por empresa, super_admin etc.), a página deve
  // mostrar só o trailer e esconder o CTA de assistir.
  const isComingSoon = course.status === 'coming_soon'

  // Player com tracking só pra matriculado em curso publicado. Visitante e
  // coming_soon caem no CourseTrailer (iframe puro, sem chamadas a /progress).
  const shouldTrack = !!(isEnrolled && !isComingSoon && videoUrl && firstEpisode?.id)
  const { data: episodeProgress } = useEpisodeProgress(firstEpisode?.id ?? 0)

  const trailerUrl = course.trailer ?? null
  const previewUrl = isComingSoon ? trailerUrl : isEnrolled && videoUrl ? videoUrl : trailerUrl

  return (
    <>
      <Header />

      <section className="bg-gradient-to-b from-background to-muted/20 py-10 md:py-16">
        <div className="container max-w-5xl">
          <Link
            to="/cursos"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Cursos
          </Link>

          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium">
              Masterclass
            </span>
            <span className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground font-medium">
              {levelLabels[course.level]}
            </span>
            {course.accessType === 'restricted' && (
              <span className="text-xs px-3 py-1.5 rounded-full bg-orange-500/10 text-orange-500 font-medium flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Acesso Restrito
              </span>
            )}
            {isComingSoon && (
              <span className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground font-medium flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Em breve
              </span>
            )}
            {isEnrolled && (
              <span className="text-xs px-3 py-1.5 rounded-full bg-green-500/10 text-green-500 font-medium flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                {allEpisodesCompleted ? 'Concluída' : 'Matriculado'}
              </span>
            )}
          </div>

          {/* Título */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {course.title}
          </h1>

          {/* Vídeo principal: matriculado em curso publicado → YouTubePlayer
              com tracking (registra watchTime e auto-completa em 90%). Demais
              casos (visitante, não matriculado, coming_soon) → CourseTrailer
              como preview, sem tracking. */}
          {shouldTrack && firstEpisode && videoUrl ? (
            <div className="mb-6">
              <div className="aspect-video rounded-xl overflow-hidden bg-card border border-border">
                <YouTubePlayer
                  videoUrl={videoUrl}
                  episodeId={firstEpisode.id}
                  initialProgress={
                    episodeProgress?.completed ? 0 : (episodeProgress?.watchTime ?? 0)
                  }
                />
              </div>
            </div>
          ) : (
            previewUrl && (
              <div className="mb-6">
                <CourseTrailer url={previewUrl} courseTitle={course.title} />
              </div>
            )
          )}

          {/* CTA de matrícula / continuar */}
          {!isEnrolled && (
            <div className="mb-8">
              {course.accessType === 'restricted' ? (
                <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-4 flex items-center gap-3">
                  <Lock className="w-5 h-5 text-orange-500 shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Masterclass com acesso restrito</p>
                    <p className="text-xs text-muted-foreground">
                      Requer aprovação do administrador.
                    </p>
                  </div>
                </div>
              ) : (
                <Button
                  size="lg"
                  onClick={onEnroll}
                  disabled={enrollPending}
                  className="w-full sm:w-auto"
                >
                  {enrollPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Matriculando…
                    </>
                  ) : isAuthenticated ? (
                    'Matricular Gratuitamente'
                  ) : (
                    'Fazer login pra acessar'
                  )}
                </Button>
              )}
            </div>
          )}
          {isEnrolled && isComingSoon && (
            <div className="mb-8">
              <div className="rounded-lg border border-border/40 bg-muted/20 p-4 flex items-center gap-3">
                <Clock className="w-5 h-5 text-muted-foreground shrink-0" />
                <div>
                  <p className="font-medium text-sm">Masterclass em breve</p>
                  <p className="text-xs text-muted-foreground">
                    Você já está matriculado. O acesso à aula será liberado quando a masterclass for
                    publicada.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Descrição */}
          <HtmlContent
            content={course.description}
            className="text-base text-muted-foreground mb-6"
          />

          {/* Meta (nível + duração) */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground py-4 border-y border-border/40 mb-8">
            <div className="flex items-center gap-2">
              <LevelIcon level={course.level} size={18} />
              <span>{levelLabels[course.level]}</span>
            </div>
            {course.duration && course.duration > 0 && (
              <div className="flex items-center gap-2">
                <Mic2 className="w-4 h-4 text-primary" />
                <span>{formatDuration(course.duration)}</span>
              </div>
            )}
          </div>

          {/* Cards lado a lado: Mentor + Workbook */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <MentorCard mentor={course.mentor} />
            <WorkbookCard
              url={workbookUrl}
              cover={workbookCover}
              isEnrolled={!!isEnrolled}
              courseTitle={course.title}
            />
          </div>

          {/* Categoria */}
          {course.category && (
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Categoria:</span> {course.category}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Sub-componentes — separados pra ficar legível, mas no mesmo arquivo
// pra evitar inflar a árvore de imports.

function MentorCard({ mentor }: { mentor: Course['mentor'] }) {
  if (!mentor) {
    return (
      <div className="rounded-xl border border-border/40 bg-muted/10 p-5 flex flex-col">
        <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-3">
          Mentor
        </div>
        <div className="flex-1 flex items-center justify-center text-center text-sm text-muted-foreground py-6">
          <div>
            <Mic2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Mentor a ser definido</p>
            <p className="text-xs opacity-70 mt-1">Atribua um mentor pelo admin do curso</p>
          </div>
        </div>
      </div>
    )
  }

  const initials = mentor.name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="rounded-xl border border-border/40 bg-card/40 backdrop-blur-sm p-5 flex flex-col">
      <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-3">
        Conheça seu Mentor
      </div>
      <div className="flex items-center gap-3 mb-3">
        {mentor.photo ? (
          <img
            src={mentor.photo}
            alt={mentor.name}
            className="w-14 h-14 rounded-full object-cover border-2 border-primary/40"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold border-2 border-primary/40">
            {initials}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{mentor.name}</p>
          {(mentor.role || mentor.company) && (
            <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
              <Briefcase className="w-3 h-3 shrink-0" />
              {[mentor.role, mentor.company].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      </div>
      {mentor.bio && (
        // Sem `line-clamp` — o bio é limitado a 500 caracteres no cadastro
        // do mentor, então cabe inteiro sem precisar de truncamento.
        <p className="text-xs text-muted-foreground leading-relaxed mb-3 flex-1">{mentor.bio}</p>
      )}
      {(mentor.linkedin || mentor.instagram) && (
        <div className="flex gap-2 mt-auto">
          {mentor.linkedin && (
            <a
              href={mentor.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border/50 hover:border-primary hover:text-primary transition-colors"
            >
              <Linkedin className="w-3.5 h-3.5" />
              LinkedIn
            </a>
          )}
          {mentor.instagram && (
            <a
              href={
                mentor.instagram.startsWith('http')
                  ? mentor.instagram
                  : `https://instagram.com/${mentor.instagram.replace(/^@/, '')}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border/50 hover:border-primary hover:text-primary transition-colors"
            >
              <Instagram className="w-3.5 h-3.5" />
              Instagram
            </a>
          )}
        </div>
      )}
    </div>
  )
}

function WorkbookCard({
  url,
  cover,
  isEnrolled,
  courseTitle,
}: {
  url: string | null
  cover: string | null
  isEnrolled: boolean
  courseTitle: string
}) {
  if (!url) {
    return (
      <div className="rounded-xl border border-border/40 bg-muted/10 p-5 flex flex-col">
        <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-3">
          Workbook
        </div>
        <div className="flex-1 flex items-center justify-center text-center text-sm text-muted-foreground py-6">
          <div>
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Sem workbook ainda</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border/40 bg-card/40 backdrop-blur-sm p-5 flex flex-col">
      <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-3">
        Workbook
      </div>
      {cover ? (
        <img
          src={cover}
          alt={`Workbook ${courseTitle}`}
          className="aspect-video w-full object-cover rounded-md border border-border/30 mb-3"
        />
      ) : (
        <div className="aspect-video w-full bg-muted/30 rounded-md border border-border/30 mb-3 flex items-center justify-center text-muted-foreground">
          <FileText className="w-10 h-10 opacity-40" />
        </div>
      )}
      <p className="text-xs text-muted-foreground mb-3 leading-relaxed flex-1">
        Material complementar pra acompanhar a aula e fazer anotações.
      </p>
      {isEnrolled ? (
        <div className="flex flex-col gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border/50 hover:border-primary hover:text-primary transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Visualizar PDF
          </a>
          <a
            href={url}
            download
            className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Baixar Workbook
          </a>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground italic">
          Matricule-se pra liberar o workbook
        </p>
      )}
    </div>
  )
}
